import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { activateAccount, requestActivationOtp } from "@/features/auth/services/authService";
import { useAuth } from "@/features/auth/context/AuthContext";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";

const errorCode = (error: unknown): string => {
  if (!axios.isAxiosError(error)) return "";
  return error.response?.data?.error_code ?? error.response?.data?.code ?? "";
};

export const ActivateAccountPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, logout, userData, userToken, streamToken, roles } = useAuth();
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => setCooldown((value) => Math.max(0, value - 1)), 1_000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const clearPendingAndLeave = () => {
    if (userData && userToken) {
      login(userToken, streamToken ?? "", { ...userData, emailPending: false }, roles);
    }
    navigate("/", { replace: true });
  };

  const sendCode = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    try {
      await requestActivationOtp(email);
      setStep(2);
      setCooldown(60);
    } catch (requestError) {
      const code = errorCode(requestError);
      if (code === "activation_not_required") clearPendingAndLeave();
      else if (code === "register_email_exists") setError(t("activate.email_in_use"));
      else setError(t("activate.generic_error"));
    } finally {
      setLoading(false);
    }
  };

  const submitActivation = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setError(t("activate.code_invalid"));
      return;
    }
    if (!password || password !== confirmPassword) {
      setError(t("activate.password_mismatch"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await activateAccount(email, otp, password);
      login(data.token, data.streamToken ?? "", data.user, data.roles ?? roles);
      navigate("/", { replace: true });
    } catch (activationError) {
      const code = errorCode(activationError);
      setError(code === "otp_invalid" ? t("activate.code_invalid") : t("activate.generic_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8">
        <h1 className="text-2xl font-bold text-indigo-600">{t("activate.title")}</h1>
        <p className="mt-2 text-sm text-gray-500">{t("activate.subtitle")}</p>

        <form className="mt-6 space-y-4" onSubmit={step === 1 ? (event) => { event.preventDefault(); void sendCode(); } : submitActivation}>
          <Input label={t("activate.email_label")} type="email" value={email} onChange={(event) => setEmail(event.target.value)} disabled={step === 2} />
          {step === 2 && (
            <>
              <Input label={t("activate.code_label")} type="text" inputMode="numeric" maxLength={6} value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, ""))} />
              <Input label={t("activate.new_password")} type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
              <Input label={t("activate.confirm_password")} type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
            </>
          )}
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">{error}</p>}
          <Button type="submit" loading={loading} className="w-full">
            {t(step === 1 ? "activate.send_code" : "activate.activate_button")}
          </Button>
        </form>

        {step === 2 && (
          <button type="button" disabled={cooldown > 0 || loading} onClick={() => void sendCode()} className="mt-4 text-sm text-indigo-600 disabled:text-gray-400">
            {cooldown > 0 ? t("activate.resend_in", { seconds: cooldown }) : t("activate.resend")}
          </button>
        )}
        <button type="button" onClick={logout} className="mt-6 block text-sm text-gray-500 hover:text-gray-700">
          {t("activate.logout")}
        </button>
      </div>
    </main>
  );
};
